import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import classnames from 'classnames';
import { TrackingHandler } from 'client/tracking/handler';
import { CmsEntities } from 'client/data/models/cms';
import { getFullImageUrl } from 'client/utils/image-helpers';
import { ImpressionTracker } from 'site-modules/shared/components/impression-tracker/impression-tracker';
import { WEBSITE_CLICKOUT_CUSTOM } from 'site-modules/shared/constants/ads';
import { CompareAd } from 'site-modules/shared/components/native-ad/compare-ad/compare-ad';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { CtaButtonText } from 'site-modules/shared/components/native-ad/native-ad-components/cta-button-text/cta-button-text';

import './cpo-ad.scss';

const getMetadataValue = (entry, name, defaultName) =>
  entry.metadata(name).value() || entry.metadata(defaultName).value();

const handleClick = ({ currentTarget: { dataset } }) =>
  TrackingHandler.fireEvent(
    WEBSITE_CLICKOUT_CUSTOM,
    {},
    {
      action_cause: 'link_click',
      value: dataset.trackingValue,
    }
  );

export function CpoAd({ entry, className, isMobile }) {
  const platform = isMobile ? 'mobile' : 'wired';
  const oemClickOutUrl = getMetadataValue(entry, `oemClickOutUrl-${platform}`, 'oemClickOutUrl');
  if (!oemClickOutUrl) {
    return null;
  }

  const { title } = entry;
  const copyText = getMetadataValue(entry, `copy-text-${platform}`, 'copy-text');
  const impressionTracker = getMetadataValue(entry, `impression-tracker-${platform}`, 'impression-tracker');
  const oemClickOutText = getMetadataValue(entry, `oemClickOutText-${platform}`, 'oemClickOutText');
  const oemDisplayUrl = getMetadataValue(entry, 'oemDisplayUrl');
  const sponsoredTitle = getMetadataValue(entry, 'sponsored-title');
  const sponsoredBrandImage = getFullImageUrl(getMetadataValue(entry, 'sponsored-brand-image'));
  const sponsoredVehicleImage = getFullImageUrl(getMetadataValue(entry, 'sponsored-vehicle-image'));

  return (
    <Fragment>
      <div
        className={classnames(
          'cpo-ad card overflow-hidden',
          { 'px-0_75 py-2': isMobile, 'px-3 py-1': !isMobile },
          className
        )}
      >
        <div className={classnames('pos-a ellipse', { mobile: isMobile, wired: !isMobile })} />
        <Row>
          <Col xs={12} md={4} className="d-flex justify-content-center">
            <img
              src={sponsoredVehicleImage}
              alt=""
              style={{ maxHeight: '145px' }}
              className={classnames({ 'mb-1_25': isMobile })}
            />
          </Col>
          <Col
            xs={12}
            md={4}
            className={classnames('d-flex flex-column text-cool-gray-30', {
              'align-items-center': isMobile,
              'justify-content-center': !isMobile,
            })}
          >
            <div
              className={classnames('text-cool-gray-50 medium d-flex align-items-center', {
                'mb-1_25': isMobile,
                'mb-0_5': !isMobile,
              })}
            >
              {sponsoredTitle}
              <img src={sponsoredBrandImage} alt="" className="ms-0_5" />
            </div>
            <ContentFragment classes="heading-4 mb-0_5">{title}</ContentFragment>
            <ContentFragment classes={classnames({ 'mb-1_25 text-center': isMobile })}>{copyText}</ContentFragment>
          </Col>
          <Col
            xs={12}
            md={4}
            className={classnames('d-flex', {
              'justify-content-center': isMobile,
              'align-items-center justify-content-end': !isMobile,
            })}
          >
            <a
              href={oemClickOutUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClick}
              data-tracking-value={oemClickOutText}
            >
              <div className={classnames('btn size-16 px-2 btn-outline-blue-50 fw-normal text-transform-none')}>
                <CtaButtonText text={oemClickOutText} />
              </div>
              {oemDisplayUrl && <div className="text-blue-50 small text-center">{oemDisplayUrl}</div>}
            </a>
          </Col>
        </Row>
      </div>
      <CompareAd isMobile={isMobile} />
      {impressionTracker && <ImpressionTracker src={impressionTracker} className="impression" />}
    </Fragment>
  );
}

CpoAd.propTypes = {
  entry: CmsEntities.Content.isRequired,
  className: PropTypes.string,
  isMobile: PropTypes.bool,
};

CpoAd.defaultProps = {
  className: null,
  isMobile: false,
};
